// Feature Tour Types for interactive app walkthrough with React Joyride
// Copy managed in the i18n dictionaries (src/i18n/app/*.ts, `tour.*`).

import type { Step, Placement } from 'react-joyride';
import type { AppTranslations } from '@/i18n/app-translations.types';

export interface FeatureTourProgress {
  currentStepIndex: number;
  completedSteps: number[];
  isCompleted: boolean;
  startedAt?: string;
  completedAt?: string;
  lastInteractionAt?: string;
}

export interface ChecklistItem {
  key: ChecklistItemKey;
  label: string;
  description: string;
  isCompleted: boolean;
  completedAt?: string;
  icon: string;
}

export type ChecklistItemKey =
  | 'first_round'
  | 'join_event'
  | 'complete_profile'
  | 'first_post'
  | 'connect_member'
  | 'setup_equipment';

// Extended Joyride Step with additional metadata
export interface TourStep extends Omit<Step, 'target'> {
  target: string; // CSS selector for the element to highlight
  content: React.ReactNode | string;
  title?: string;
  disableBeacon?: boolean;
  placement?: Placement;
  actionRequired?: boolean; // If true, user must click the highlighted element
  checklistKey?: ChecklistItemKey; // Link step to checklist item
}

// Interactive tour steps. Every target is a bottom-nav tab, which exists on any page
// that shows the bar, so the tour can never stall waiting for a missing element.
// Copy lives in the i18n dictionaries under `tour.*`.
export function getInteractiveTourSteps(t: AppTranslations): TourStep[] {
  const tour = t.tour;
  return [
    { target: 'body', title: tour.welcomeTitle, content: tour.welcomeBody, placement: 'center', disableBeacon: true },
    { target: '[data-tour="practice-nav"]', title: tour.practiceTitle, content: tour.practiceBody, placement: 'top', disableBeacon: true },
    { target: '[data-tour="coach-nav"]', title: tour.coachTitle, content: tour.coachBody, placement: 'top', disableBeacon: true },
    { target: '[data-tour="progress-nav"]', title: tour.progressTitle, content: tour.progressBody, placement: 'top', disableBeacon: true },
    { target: '[data-tour="club-nav"]', title: tour.clubTitle, content: tour.clubBody, placement: 'top', disableBeacon: true },
    { target: '[data-tour="profile-nav"]', title: tour.profileTitle, content: tour.profileBody, placement: 'top', disableBeacon: true },
    { target: 'body', title: tour.doneTitle, content: tour.doneBody, placement: 'center', disableBeacon: true },
  ];
}

// Default checklist items
export const DEFAULT_CHECKLIST_ITEMS: Omit<ChecklistItem, 'isCompleted' | 'completedAt'>[] = [
  {
    key: 'first_round',
    label: 'Play your first tracked round',
    description: 'Start a round to begin tracking your game',
    icon: '⛳'
  },
  {
    key: 'join_event',
    label: 'Join a club tournament',
    description: 'Register for an upcoming competition',
    icon: '🏆'
  },
  {
    key: 'connect_member',
    label: 'Connect with a playing partner',
    description: 'Add a friend to your golf network',
    icon: '👥'
  },
  {
    key: 'setup_equipment',
    label: 'Set up your club distances',
    description: 'Add your clubs with yardages',
    icon: '🏌️'
  },
  {
    key: 'first_post',
    label: 'Share your first achievement',
    description: 'Post something to the social feed',
    icon: '📱'
  }
];

// LocalStorage keys
export const FEATURE_TOUR_STORAGE_KEY = 'featureTourProgress';
export const CHECKLIST_STORAGE_KEY = 'featureTourChecklist';
